'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { enqueueSnackbar } from 'notistack'
import { Box, Container, Grid, Typography } from '@mui/material'

// Types
import type { FieldValues } from 'react-hook-form'
import type { TurnstileInstance } from '@marsidev/react-turnstile'
import type { APIFormSubmissionCreateResult } from '@/types/api/(builder)/form-submission/create'
import type { CareersApplyPageProps } from '@/types/components/pages/main/careers-apply'

// Utils
import URLParams from '@/utils/api/url-params'
import ToEnglishNumber from '@/utils/number/to-english-number'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const EyebrowTypographyAtom = dynamic(() => import('@/components/atoms/typography/eyebrow'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const CareersFormOrganism = dynamic(() => import('@/components/organisms/forms/main/careers'))
const FooterOrganism = dynamic(() => import('@/components/organisms/footer'))

// Backend form that stores applications (form builder alias)
const FORM_ALIAS = 'careers'

// Application page: the form, then a thank-you once the CV is sent
const CareersApplyPage = (props: CareersApplyPageProps) => {
	// Props
	const { lng, locale, position } = props

	// Variables
	const content = locale.content.careers
	const [loading, setLoading] = useState<boolean>(false)
	const [sent, setSent] = useState<boolean>(false)
	const turnstileRef = useRef<TurnstileInstance | undefined>(undefined)

	// Form
	const form = useForm<FieldValues>({ mode: 'onTouched', defaultValues: { position } })

	// Callbacks
	const send = useCallback(async () => {
		// Stop on invalid fields
		if (!(await form.trigger())) return enqueueSnackbar(content.form.invalid, { variant: 'error', autoHideDuration: 6000 })

		// Turnstile tokens are single-use and expire; read a fresh one
		const turnstileToken = turnstileRef.current?.isExpired() ? undefined : turnstileRef.current?.getResponse()

		if (!turnstileToken) return enqueueSnackbar(content.form.captcha, { variant: 'error', autoHideDuration: 6000 })

		// Enable loading
		setLoading(true)

		// Every value as text; empty fields are left out
		const values = form.getValues()
		const body = Object.fromEntries(
			Object.entries(values)
				.filter(([, value]) => value !== undefined && value !== '' && !(Array.isArray(value) && !value.length))
				.map(([key, value]) => [key, Array.isArray(value) ? value.join('، ') : ToEnglishNumber(String(value))])
		)

		try {
			const res = await fetch(`/api/form-submission/create?${URLParams({ form_alias: FORM_ALIAS, turnstile_token: turnstileToken })}`, {
				method: 'PUT',
				headers: { 'Accept-Language': lng, 'Content-Type': 'application/json' },
				body: JSON.stringify(body)
			})

			const data: APIFormSubmissionCreateResult = await res.json()

			if (data.status) setSent(true)
			else {
				turnstileRef.current?.reset()
				enqueueSnackbar(data.message ?? content.form.error, { variant: 'error', autoHideDuration: 6000 })
			}
		} catch {
			turnstileRef.current?.reset()
			enqueueSnackbar(content.form.error, { variant: 'error', autoHideDuration: 6000 })
		}

		// Disable loading
		setLoading(false)
	}, [lng, form, content])

	return (
		<Box sx={{ width: 1, bgcolor: '#e9ecef', overflowX: 'clip' }}>
			<Container maxWidth="lg" sx={{ pt: { xs: 4, md: 4 }, pb: { xs: 8, md: 8 } }}>
				<Grid container spacing={{ xs: 4, md: 5 }} sx={{ alignItems: 'flex-start' }}>
					<Grid size={{ xs: 12, md: 4 }} sx={{ position: { md: 'sticky' }, top: { md: 110 } }}>
						<RevealAtom>
							<EyebrowTypographyAtom text={content.eyebrow} />

							<Typography component="h1" sx={{ mt: 2, fontSize: { xs: 28, md: 32 }, fontWeight: 700, lineHeight: 1.45 }}>
								{content.title}
							</Typography>

							<Typography sx={{ mt: 2, fontSize: { xs: 15, md: 16 }, lineHeight: 1.9, color: 'text.secondary' }}>{content.subtitle}</Typography>
						</RevealAtom>
					</Grid>

					<Grid size={{ xs: 12, md: 8 }}>
						<RevealAtom delay={0.1}>
							<Box sx={{ bgcolor: 'common.white', borderRadius: { xs: 5, md: 8 }, p: { xs: 3, md: 4 }, boxShadow: '0 1px 2px rgba(23, 23, 23, 0.04)' }}>
								{sent ? (
									<Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
										<Typography component="h2" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: 'primary.main' }}>
											{content.success.title}
										</Typography>

										<Typography sx={{ mt: 2, fontSize: 16, lineHeight: 2, color: 'text.secondary' }}>{content.success.description}</Typography>

										<Box sx={{ mt: 4 }}>
											<Link href={`/${lng}/careers`}>
												<PrimaryButtonAtom text={content.success.back} color="primary" size="large" sx={{ px: 4 }} />
											</Link>
										</Box>
									</Box>
								) : (
									<CareersFormOrganism lng={lng} locale={locale} control={form.control} loading={loading} turnstileRef={turnstileRef} onSubmit={send} />
								)}
							</Box>
						</RevealAtom>
					</Grid>
				</Grid>
			</Container>

			<FooterOrganism lng={lng} locale={locale} />
		</Box>
	)
}

export default CareersApplyPage
